// Shared meeting-options state + handlers — ported verbatim from
// App.js so /v2 (and later App.js itself) can call into the same logic.
import { useState, useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { writeTextToClipboard as writeToClipboard } from '../utils/meetingExport';

function capitalizeDate(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

export function useMeetingOptions({
  selectedCities = [],
  isSupporter = false,
  use24HourFormat = false,
  getLocalizedCityName = (c) => c.name,
  i18nLanguage = 'en',
  t = (k, opts) => k,
  defaultMeetingDuration = 60,
} = {}) {
  const [meetingOptions, setMeetingOptions] = useState([]);
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingDescription, setMeetingDescription] = useState('');
  const [showTimeControls, setShowTimeControls] = useState({});

  const formatMeetingOptionText = useCallback((option) => {
    let formattedText = '';
    option.selectedCities.forEach(city => {
      if (!city) return;
      const startTimeAtSlot = option.startDateTime.setZone(city.timezone);
      const endTimeAtSlot = option.endDateTime.setZone(city.timezone);
      let timeStr;
      if (use24HourFormat) {
        timeStr = `${startTimeAtSlot.toFormat('HH:mm')}-${endTimeAtSlot.toFormat('HH:mm')}`;
      } else if ((startTimeAtSlot.hour < 12 && endTimeAtSlot.hour < 12) ||
                 (startTimeAtSlot.hour >= 12 && endTimeAtSlot.hour >= 12)) {
        timeStr = `${startTimeAtSlot.toFormat('h:mm')}-${endTimeAtSlot.toFormat('h:mma')}`;
      } else {
        timeStr = `${startTimeAtSlot.toFormat('h:mma')}-${endTimeAtSlot.toFormat('h:mma')}`;
      }
      const dateStr = capitalizeDate(startTimeAtSlot.setLocale(i18nLanguage).toFormat('EEE, MMM d yyyy'));
      formattedText += `• ${getLocalizedCityName(city)} - ${timeStr} (${city.timezoneName || ''}) - ${dateStr}\n`;
    });
    return formattedText;
  }, [use24HourFormat, i18nLanguage, getLocalizedCityName]);

  const formatAllMeetingOptions = useCallback(() => {
    if (meetingOptions.length === 0) return '';
    let formattedText = '';
    if (meetingDescription.trim()) {
      formattedText += `${meetingDescription}\n\n`;
    }
    meetingOptions.forEach((option, index) => {
      formattedText += `${t('meeting.optionLabel', { number: index + 1 })}\n${option.text}\n`;
    });
    if (!isSupporter) {
      formattedText += t('email.footer');
    }
    return formattedText;
  }, [meetingOptions, meetingDescription, isSupporter, t]);

  // Build a one-shot formatted block for a hovered slot — same shape as
  // the action modal's Copy/Gmail use, used to seed a meeting option.
  const formatSlotBlock = useCallback((slotDateTime) => {
    if (!slotDateTime) return '';
    let text = '';
    selectedCities.forEach(city => {
      if (!city) return;
      const at = slotDateTime.setZone(city.timezone);
      const timeStr = use24HourFormat ? at.toFormat('HH:mm') : at.toFormat('h:mma');
      const dateStr = capitalizeDate(at.setLocale(i18nLanguage).toFormat('EEE, MMM d yyyy'));
      text += `• ${getLocalizedCityName(city)} - ${timeStr} (${city.timezoneName || ''}) - ${dateStr}\n`;
    });
    if (!isSupporter) text += t('email.footer');
    return text;
  }, [selectedCities, use24HourFormat, i18nLanguage, getLocalizedCityName, isSupporter, t]);

  const addMeetingOption = useCallback((slotDateTime) => {
    if (!slotDateTime) return;
    const formattedText = formatSlotBlock(slotDateTime);
    if (!formattedText) return;
    const startDateTime = slotDateTime.set({ minute: 0 });
    const endDateTime = startDateTime.plus({ minutes: defaultMeetingDuration });
    const newOption = {
      id: Date.now(),
      text: isSupporter
        ? formattedText
        : formattedText.replace('\n' + t('email.footer'), ''),
      startDateTime,
      endDateTime,
      duration: defaultMeetingDuration,
      selectedCities,
    };
    // Re-format using the row helper so the start-end ranges land identically.
    newOption.text = formatMeetingOptionText(newOption);
    setMeetingOptions(prev => [...prev, newOption]);
  }, [formatSlotBlock, defaultMeetingDuration, isSupporter, t, selectedCities, formatMeetingOptionText]);

  const removeMeetingOption = useCallback((id) => {
    setMeetingOptions(prev => prev.filter(o => o.id !== id));
  }, []);

  const updateMeetingTime = useCallback((id, hour, minute, duration) => {
    setMeetingOptions(prev => prev.map(option => {
      if (option.id !== id) return option;
      const newStart = option.startDateTime.set({ hour: parseInt(hour, 10), minute: parseInt(minute, 10) });
      const newEnd = newStart.plus({ minutes: parseInt(duration, 10) });
      const updated = { ...option, startDateTime: newStart, endDateTime: newEnd, duration: parseInt(duration, 10) };
      updated.text = formatMeetingOptionText(updated);
      return updated;
    }));
  }, [formatMeetingOptionText]);

  const toggleTimeControls = useCallback((id) => {
    setShowTimeControls(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const closeMeetingOptions = useCallback(() => {
    setMeetingOptions([]);
    setMeetingTitle('');
    setMeetingDescription('');
    setShowTimeControls({});
  }, []);

  const copyAllOptions = useCallback(() => writeToClipboard(formatAllMeetingOptions()), [formatAllMeetingOptions]);

  const sendOptionsToGmail = useCallback(() => {
    const text = formatAllMeetingOptions();
    if (!text) return;
    const subject = meetingTitle.trim() || t('email.subjectOptions');
    const url = `https://mail.google.com/mail/u/0/?fs=1&to=&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}&ui=2&tf=cm`;
    window.open(url, '_blank', 'noopener');
  }, [formatAllMeetingOptions, meetingTitle, t]);

  const createSingleGCalEvent = useCallback((option, index) => {
    const start = option.startDateTime.toUTC().toFormat("yyyyMMdd'T'HHmmss'Z'");
    const end = option.endDateTime.toUTC().toFormat("yyyyMMdd'T'HHmmss'Z'");
    let title = meetingTitle.trim() || t('calendar.eventTitleOption', { number: index + 1 });
    if (meetingOptions.length > 1 && meetingTitle.trim()) {
      title = `${meetingTitle.trim()} - Option ${index + 1}`;
    }
    const details = meetingDescription.trim()
      ? `${meetingDescription}\n\n${option.text}`
      : option.text;
    const url = `https://calendar.google.com/calendar/u/0/r/eventedit?text=${encodeURIComponent(title)}&dates=${start}/${end}&details=${encodeURIComponent(details)}`;
    window.open(url, '_blank', 'noopener');
  }, [meetingTitle, meetingDescription, meetingOptions.length, t]);

  const createICalFiles = useCallback(() => {
    if (meetingOptions.length === 0) return;
    meetingOptions.forEach((option, index) => {
      const start = option.startDateTime.toUTC().toFormat("yyyyMMdd'T'HHmmss'Z'");
      const end = option.endDateTime.toUTC().toFormat("yyyyMMdd'T'HHmmss'Z'");
      const stamp = DateTime.now().toUTC().toFormat("yyyyMMdd'T'HHmmss'Z'");
      const uid = `${stamp}-${Math.random().toString(36).slice(2, 11)}-${index}@zonewise.app`;
      const title = meetingTitle.trim() || t('calendar.eventTitleOption', { number: index + 1 });
      const description = (meetingDescription.trim()
        ? `${meetingDescription}\n\n${option.text}`
        : option.text).replace(/\n/g, '\\n');
      const ics = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//ZoneWise//Calendar//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:REQUEST',
        'BEGIN:VEVENT',
        `UID:${uid}`,
        `DTSTAMP:${stamp}`,
        `DTSTART:${start}`,
        `DTEND:${end}`,
        `SUMMARY:${title}`,
        `DESCRIPTION:${description}`,
        'END:VEVENT',
        'END:VCALENDAR',
      ].join('\r\n');
      const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${title.replace(/[^a-zA-Z0-9]+/g, '-')}-${index + 1}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    });
  }, [meetingOptions, meetingTitle, meetingDescription, t]);

  return useMemo(() => ({
    meetingOptions,
    meetingTitle, setMeetingTitle,
    meetingDescription, setMeetingDescription,
    showTimeControls,
    addMeetingOption,
    removeMeetingOption,
    updateMeetingTime,
    toggleTimeControls,
    closeMeetingOptions,
    copyAllOptions,
    sendOptionsToGmail,
    createSingleGCalEvent,
    createICalFiles,
    use24HourFormat,
  }), [
    meetingOptions, meetingTitle, meetingDescription, showTimeControls,
    addMeetingOption, removeMeetingOption, updateMeetingTime, toggleTimeControls,
    closeMeetingOptions, copyAllOptions, sendOptionsToGmail, createSingleGCalEvent,
    createICalFiles, use24HourFormat,
  ]);
}
